import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import { Minus, Plus } from 'lucide-react';
import { Input } from '@/components/ui/input';
import type { FormInput } from './InputInterface';
import { Textarea } from '@/components/ui/textarea';
import { useTranslation } from '@/hooks/useTranslation';
import { RequiredLabel } from './RequiredLabel';
import { Callout } from '@/components/ui/callout';

type SimpleInputProps<T extends string | number> = FormInput<T> & {
  type?: 'text' | 'number';
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  pattern?: string;
  requiredMulti?: boolean;
  className?: string;
  textarea?: boolean;
  readOnly?: boolean;
};

export const SimpleInput = forwardRef(function SimpleInput<T extends string | number>(
  {
    name,
    label,
    value,
    onChange,
    error,
    required,
    requiredMulti = false,
    type = 'text',
    placeholder,
    min = 0,
    max = 99999,
    step = 1,
    pattern,
    className = '',
    textarea = false,
    readOnly = false,
  }: SimpleInputProps<T>,

  ref: React.Ref<{ validate: () => boolean }>
) {
  const htmlInputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null);
  const { t } = useTranslation();
  const isNumberInput = type === 'number' && !textarea;
  const numericStep = step ?? 1;

  const getNumericValue = (): number => {
    if (typeof value === 'number') {
      return value;
    }
    if (typeof value === 'string' && value.trim().length > 0) {
      const parsed = Number(value);
      return Number.isNaN(parsed) ? 0 : parsed;
    }
    if (typeof min === 'number') {
      return min;
    }
    return 0;
  };

  const clampNumericValue = (nextValue: number): number => {
    let clamped = nextValue;
    if (typeof min === 'number') {
      clamped = Math.max(min, clamped);
    }
    if (typeof max === 'number') {
      clamped = Math.min(max, clamped);
    }
    return clamped;
  };

  const focusInput = () => {
    if (htmlInputRef.current) {
      htmlInputRef.current.focus();
    }
  };

  const applyStep = (delta: number) => {
    const current = getNumericValue();
    const next = clampNumericValue(current + delta * numericStep);
    focusInput();
    onChange(next as T);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const raw = e.target.value;
    if (type === 'number') {
      if (raw.trim() === '') {
        onChange(null);
        return;
      }
      const numeric = Number(raw);
      if (Number.isNaN(numeric)) {
        return;
      }
      onChange(numeric as T);
      return;
    }

    onChange(raw as T);
  };

  // expose validate() au parent
  useImperativeHandle(ref, () => ({
    validate: () => {
      if (htmlInputRef.current) {
        const valid = htmlInputRef.current.checkValidity();
        htmlInputRef.current.reportValidity();
        return valid;
      }
      return true;
    },
    inputRef: htmlInputRef,
  }));

  return (
    <div className={`space-y-2 ${className}`}>
      <RequiredLabel
        htmlFor={name}
        label={label}
        required={required}
        requiredMulti={requiredMulti}
      />

      {!textarea && !isNumberInput && (
        <Input
          ref={htmlInputRef as React.RefObject<HTMLInputElement>}
          id={name}
          name={name}
          type={type}
          value={(value as string | number | undefined) ?? ''}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          min={min}
          max={max}
          step={step}
          pattern={pattern}
          data-required-multi={requiredMulti || undefined}
          readOnly={readOnly}
          className={readOnly ? 'bg-muted dark:bg-muted cursor-not-allowed' : ''}
        />
      )}

      {!textarea && isNumberInput && (
        <div className="flex h-9 items-stretch overflow-hidden rounded-lg border border-input bg-transparent shadow-xs transition-[color,box-shadow] focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50 dark:bg-input/30">
          <Input
            ref={htmlInputRef as React.RefObject<HTMLInputElement>}
            id={name}
            name={name}
            type="number"
            value={value ?? ''}
            onChange={handleChange}
            placeholder={placeholder}
            required={required}
            min={min}
            max={max}
            step={step}
            pattern={pattern}
            inputMode="decimal"
            data-required-multi={requiredMulti || undefined}
            readOnly={readOnly}
            className={
              readOnly
                ? 'bg-muted dark:bg-muted cursor-not-allowed'
                : 'h-full flex-1 rounded-none border-0 bg-transparent text-center text-base shadow-none focus-visible:border-0 focus-visible:ring-0 dark:bg-transparent'
            }
          />
          <div className="flex flex-col border-l border-input/60">
            <button
              type="button"
              onClick={() => applyStep(1)}
              className="flex h-full w-9 items-center justify-center border-l border-input/60 text-muted-foreground transition-colors outline-none hover:bg-muted/40 hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50"
              aria-label={t('form.numberInput.increase')}
              title={t('form.numberInput.increase')}
            >
              <Plus className="h-4 w-4" aria-hidden />
            </button>
            <button
              type="button"
              onClick={() => applyStep(-1)}
              className="flex h-full w-9 items-center justify-center border-l border-input/60 text-muted-foreground transition-colors outline-none hover:bg-muted/40 hover:text-foreground focus-visible:ring-[3px] focus-visible:ring-inset focus-visible:ring-ring/50"
              aria-label={t('form.numberInput.decrease')}
              title={t('form.numberInput.decrease')}
            >
              <Minus className="h-4 w-4" aria-hidden />
            </button>
          </div>
        </div>
      )}

      {textarea && (
        <Textarea
          ref={htmlInputRef as React.RefObject<HTMLTextAreaElement>}
          id={name}
          name={name}
          value={(value as string | number | undefined) ?? ''}
          onChange={handleChange}
          placeholder={placeholder}
          required={required}
          data-required-multi={requiredMulti || undefined}
          readOnly={readOnly}
          className={readOnly ? 'bg-muted dark:bg-muted cursor-not-allowed' : ''}
        />
      )}

      {error && (
        <Callout tone="error" size="inline">
          {error}
        </Callout>
      )}
    </div>
  );
});
